/**
 * Safe localStorage utilities
 * Handles cases where localStorage is not available (incognito mode, iframe restrictions, etc.)
 */

/**
 * Safely checks if localStorage is available
 */
export function isLocalStorageAvailable (): boolean {
  try {
    const test = '__localStorage_test__'
    localStorage.setItem(test, test)
    localStorage.removeItem(test)
    return true
  } catch (e) {
    return false
  }
}

/**
 * Safely get an item from localStorage
 * @param key - The key to retrieve
 * @param defaultValue - Optional default value if key doesn't exist or localStorage is unavailable
 * @returns The stored value, defaultValue, or null
 */
export function safeGetItem (key: string, defaultValue: string | null = null): string | null {
  try {
    if (!isLocalStorageAvailable()) {
      return defaultValue
    }
    return localStorage.getItem(key) ?? defaultValue
  } catch (error) {
    console.warn(`Error accessing localStorage for key "${key}":`, error)
    return defaultValue
  }
}

/**
 * Safely set an item in localStorage
 * @param key - The key to set
 * @param value - The value to store
 * @returns true if successful, false otherwise
 */
export function safeSetItem (key: string, value: string): boolean {
  try {
    if (!isLocalStorageAvailable()) {
      return false
    }
    localStorage.setItem(key, value)
    return true
  } catch (error) {
    console.warn(`Error setting localStorage for key "${key}":`, error)
    return false
  }
}

/**
 * Safely remove an item from localStorage
 * @param key - The key to remove
 * @returns true if successful, false otherwise
 */
export function safeRemoveItem (key: string): boolean {
  try {
    if (!isLocalStorageAvailable()) {
      return false
    }
    localStorage.removeItem(key)
    return true
  } catch (error) {
    console.warn(`Error removing localStorage for key "${key}":`, error)
    return false
  }
}

/**
 * Safely clear all localStorage
 * @returns true if successful, false otherwise
 */
export function safeClearStorage (): boolean {
  try {
    if (!isLocalStorageAvailable()) {
      return false
    }
    localStorage.clear()
    return true
  } catch (error) {
    console.warn('Error clearing localStorage:', error)
    return false
  }
}

// sessionStorage — sobrevive a navegaciones dentro de la misma pestaña pero
// se borra al cerrarla. Útil para pasar datos efímeros del flujo de auth
// (p.ej. distinguir One Tap de botón) entre páginas sin tocar el contexto.
export function safeSessionGetItem (key: string, defaultValue: string | null = null): string | null {
  try {
    return sessionStorage.getItem(key) ?? defaultValue
  } catch (error) {
    console.warn(`Error accessing sessionStorage for key "${key}":`, error)
    return defaultValue
  }
}

export function safeSessionSetItem (key: string, value: string): boolean {
  try {
    sessionStorage.setItem(key, value)
    return true
  } catch (error) {
    console.warn(`Error setting sessionStorage for key "${key}":`, error)
    return false
  }
}

export function safeSessionRemoveItem (key: string): boolean {
  try {
    sessionStorage.removeItem(key)
    return true
  } catch (error) {
    console.warn(`Error removing sessionStorage for key "${key}":`, error)
    return false
  }
}
